import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { DOCUMENT_CATEGORY, DocumentEntry, DocumentFile } from '../../../../api/app-types/document.app';
import { LvrDocumentItem } from '../../../../core/components/document-item/document-item';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { MatTableModule } from '@angular/material/table';
import { Paginator } from '../../../../core/components/paginator/paginator';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { provideUploadApi, UPLOAD_API } from '../../../../api/services/upload.api';
import { PageEvent } from '@angular/material/paginator';
import { FilesQuery } from '../../../../api/api-types/document.api';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe } from '@jsverse/transloco';
import { LvrField } from '../../../../core/components/field/field';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import {
	buildDocumentNavItems,
	documentCategoryOrder,
	mapDocumentCategoryToKey,
} from '../../../../shared/helpers/documents.func';
import { REPORT_TYPE } from '../../../../api/app-types/report-document.app';
import { formatDateToDisplay } from '../../../../shared/helpers/date.func';
import { downloadDocumentFiles } from '../../../../shared/helpers/document-download.func';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-documents-list',
	imports: [TranslocoPipe, LvrDocumentItem, MatTableModule, Paginator, MatInputModule, MatSelectModule, LvrField],
	providers: [provideUploadApi],
	templateUrl: './documents-list.html',
	styleUrl: './documents-list.scss',
})
export class DocumentsList extends LvrRedirectNav {
	private readonly api = inject(UPLOAD_API);
	private readonly env = inject(ENVIRONMENT);
	private readonly session = inject(SessionManagerService);
	public readonly displayedColumns = ['name', 'date', 'files', 'category'];
	public readonly mapDocumentCategoryToKey = mapDocumentCategoryToKey;
	public readonly DOCUMENT_CATEGORY = DOCUMENT_CATEGORY;
	private readonly canReadContactDocuments = hasPermission(this.session.permissions, [
		'Clients.Read',
		'Clients.Read.PhaseOneOnly',
	]);
	private readonly canReadDisabilityKindDocuments = hasPermission(this.session.permissions, [
		'Clients.Read',
		'Clients.Read.PhaseOneOnly',
	]);

	private readonly allowedDocumentCategories = computed(() => {
		const categories: DOCUMENT_CATEGORY[] = [
			DOCUMENT_CATEGORY.ASSIGNMENT,
			DOCUMENT_CATEGORY.DISABILITY_REPORT,
			DOCUMENT_CATEGORY.REPORT,
		];

		if (this.canReadDisabilityKindDocuments()) {
			categories.push(DOCUMENT_CATEGORY.DISABILITY_KIND);
		}

		if (this.canReadContactDocuments()) {
			categories.push(DOCUMENT_CATEGORY.CONTACT);
		}

		return categories;
	});

	public readonly DOCUMENT_FILTER_OPTIONS = computed(() => this.allowedDocumentCategories());
	public readonly clientId = input.required<number>();
	public readonly initialDocumentsPage = input.required<PaginatedResponse<DocumentEntry>>();

	public readonly selectedCategories = signal<Set<DOCUMENT_CATEGORY>>(new Set());
	public readonly documentsPage = signal<PaginatedResponse<DocumentEntry>>({
		entities: [],
		offset: 0,
		range: 0,
		totalEntities: 0,
	});
	public readonly loading = signal(false);
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.env.defaultPageSize);

	public constructor() {
		super();
		effect(() => {
			const initialPage = this.initialDocumentsPage();

			this.documentsPage.set(initialPage);
			this.pageSize.set(initialPage.range || this.env.defaultPageSize);
			this.pageIndex.set(initialPage.offset);
		});
	}

	public readonly totalItems = computed(() => this.documentsPage().totalEntities);
	public readonly documentsRaw = computed(() => this.documentsPage().entities);
	public readonly displayedDocuments = computed(() => {
		const docs = this.documentsRaw();
		const selected = this.selectedCategories();
		const allowed = new Set(this.allowedDocumentCategories());

		const visibleDocs = docs.filter((d) => allowed.has(d.category));
		const filtered = !selected.size ? visibleDocs : visibleDocs.filter((d) => selected.has(d.category));

		return [...filtered].sort((a, b) => {
			const ca = documentCategoryOrder[a.category];
			const cb = documentCategoryOrder[b.category];
			if (ca !== cb) return ca - cb;
			return b.createdAt.getTime() - a.createdAt.getTime();
		});
	});

	public async onCategoryFilterChange(values: DOCUMENT_CATEGORY[]) {
		const allowed = new Set(this.allowedDocumentCategories());
		const filteredValues = values.filter((value) => allowed.has(value));

		this.selectedCategories.set(new Set(filteredValues));

		// Reset pagination to first page
		this.pageIndex.set(0);

		await this.loadPage(0, this.pageSize());
	}

	public readonly selectedCategoriesArray = computed(() => Array.from(this.selectedCategories()));

	public async handlePageEvent(event: PageEvent): Promise<void> {
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);

		await this.loadPage(event.pageIndex, event.pageSize);
	}

	public onFileClick(file: DocumentFile) {
		const entries = this.displayedDocuments();

		const entry = entries.find((e) => (e.externalFileReference ?? []).some((f) => f.id === file.id));

		const navItems = entry ? buildDocumentNavItems([entry]) : [];

		this.navigateAfterFinished([file.id], {
			relativeTo: this.route,
			state: { navItems },
		});
	}

	public onReportClick(entry: DocumentEntry) {
		if ((entry.externalFileReference ?? []).length > 0) {
			downloadDocumentFiles(entry.externalFileReference);
			return;
		}
		const reportTypeSegments = this.parseReportTypeToUrlSegments(entry.reportTypeId);

		this.navigateAfterFinished([
			'desk',
			'client',
			this.clientId(),
			'assignments',
			entry.assignmentId,
			...reportTypeSegments,
			entry.id,
		]);
	}

	public formatDate(date: Date) {
		const displayDate = formatDateToDisplay(date);
		return displayDate === '31.12.1' ? '---' : displayDate;
	}

	private parseReportTypeToUrlSegments(id?: DOCUMENT_CATEGORY | number): string[] {
		switch (id) {
			case REPORT_TYPE.TECHNICAL_START_REPORT:
			case REPORT_TYPE.START_REPORT:
				return ['report'];
			case REPORT_TYPE.IMPEDIMENT_REPORT:
				return ['report', 'impediment'];
			case REPORT_TYPE.END_REPORT:
				return ['end-report'];
			case REPORT_TYPE.FDS:
				return ['fds', 'template'];
			case REPORT_TYPE.ADMISSION_QB_REPORT:
				return ['admission-qb-report'];
			case REPORT_TYPE.END_QB_REPORT:
				return ['end-qb-report'];
			default:
				return [];
		}
	}

	/**
	 * Builds a FilesQuery object based on the currently selected
	 * document categories and the given pagination parameters.
	 */
	private buildFilesQuery(offset: number, range: number) {
		const selected = this.selectedCategories();
		const hasFilter = selected.size > 0;

		const canReadContact = this.canReadContactDocuments();
		const canReadDisabilityKind = this.canReadDisabilityKindDocuments();

		const fileQuery: FilesQuery = {
			assignment: hasFilter ? selected.has(DOCUMENT_CATEGORY.ASSIGNMENT) : true,
			contact: canReadContact ? (hasFilter ? selected.has(DOCUMENT_CATEGORY.CONTACT) : true) : false,
			disabilityKind: canReadDisabilityKind
				? hasFilter
					? selected.has(DOCUMENT_CATEGORY.DISABILITY_KIND)
					: true
				: false,
			disabilityReport: hasFilter ? selected.has(DOCUMENT_CATEGORY.DISABILITY_REPORT) : true,
			report: hasFilter ? selected.has(DOCUMENT_CATEGORY.REPORT) : true,
			offset,
			range,
		};

		return fileQuery;
	}

	/**
	 * Fetches a page of client documents from the API based on the current
	 * filters and pagination, and updates the local documents state.
	 */
	private async loadPage(offset: number, range: number) {
		this.loading.set(true);

		try {
			const fileQuery = this.buildFilesQuery(offset, range);
			const response = await this.api.getClientFiles(this.clientId(), fileQuery);
			this.documentsPage.set(response.entries);
		} finally {
			this.loading.set(false);
		}
	}
}
